import { cloneDeep } from 'lodash-es'

import { ComponentTypes, type HiddenElementsInCanvas, type SectionListSection } from '@canvas-builder/types/canvas'
import { createNodeId } from '@canvas-builder/util/canvas.util'

type Action<T> = (n: T) => void
type TreeLike = { id: string; children?: TreeLike[]; type?: ComponentTypes; data?: any }
type Predicate<T> = (n: T) => boolean

export function applyToTreeBy<T extends TreeLike>(
  tree: T[],
  action: Action<T>,
  condition: Predicate<T> = () => true,
): void {
  for (const node of tree) {
    if (condition(node)) {
      action(node)
    }
    if (node.children) {
      applyToTreeBy(node.children as T[], action, condition)
    }
  }
}

export function findNodeInTreeById<T extends TreeLike>(tree: T[], value: string | number, reverse = false): T | null {
  const stack = [...tree]
  while (stack.length) {
    const node = stack[reverse ? 'pop' : 'shift']()
    if (node?.id === value) return node
    node?.children && stack.push(...(node.children as T[]))
  }
  return null
}

export function findNodeInTreeByType<T extends TreeLike>(tree: T[], type: ComponentTypes, reverse = false): T | null {
  const stack = [...tree]
  while (stack.length) {
    const node = stack[reverse ? 'pop' : 'shift']()
    if (node?.type === type) return node
    node?.children && stack.push(...(node.children as T[]))
  }
  return null
}

export function findNodeInTreeByCondition<T extends TreeLike>(
  tree: T[],
  condition: Predicate<T>,
  reverse = false,
): T | null {
  const stack = [...tree]
  while (stack.length) {
    const node = stack[reverse ? 'pop' : 'shift']()
    if (node && condition(node)) return node
    node?.children && stack.push(...(node.children as T[]))
  }
  return null
}

export function filterTreeBy<T extends TreeLike>(tree: T[], condition: Predicate<T> = (n) => !!n.id): T[] {
  const filteredNodes: T[] = []

  for (const node of tree) {
    if (condition(node)) {
      const children = node.children ? filterTreeBy(node.children as T[], condition) : undefined

      filteredNodes.push({ ...node, children })
    }
  }

  return filteredNodes
}

export function collectAllNodesByCondition<T extends TreeLike>(tree: T[], condition: Predicate<T>): T[] {
  const collectedNodes: T[] = []

  function traverse(nodes: T[]) {
    for (const node of nodes) {
      if (condition(node)) {
        collectedNodes.push(node)
      }
      if (node.children) {
        traverse(node.children as T[])
      }
    }
  }

  traverse(tree)
  return collectedNodes
}

export function findParentByNodeId<T extends TreeLike>(tree: T[], value: string | number): T | null {
  let parent: T | null = null

  function traverse(nodes: T[]) {
    for (const node of nodes) {
      if (node.children) {
        if (node.children.some((child) => child.id === value)) {
          parent = node
          return true
        } else {
          const found = traverse(node.children as T[])
          if (found) return true
        }
      }
    }
    return false
  }

  traverse(tree)
  return parent
}

export function regenerateTreeIds<T extends TreeLike>(tree: T[]): T[] {
  const newTree: T[] = []

  for (const node of tree) {
    const newId = createNodeId(node.type)
    const children = node.children ? regenerateTreeIds(node.children as T[]) : undefined

    newTree.push({ ...node, id: newId, children })
  }

  return newTree
}

export function someNodeInTree<T extends TreeLike>(tree: T[], condition: Predicate<T> = (n) => !!n.id): boolean {
  for (const node of tree) {
    if (condition(node)) {
      return true
    }

    if (node.children && someNodeInTree(node.children as T[], condition)) {
      return true
    }
  }

  return false
}

export function skipElementsInTree<T extends TreeLike>(
  tree: T[],
  idsToHide: {
    sections: HiddenElementsInCanvas
    components: HiddenElementsInCanvas
  },
): T[] {
  const localTree = tree.filter((node) => !(node.id in idsToHide.components))

  for (const node of localTree) {
    if (node.type === ComponentTypes.SectionList && node.data?.sections) {
      node.data.sections = node.data.sections.filter(
        (section: SectionListSection) => !(section.id in idsToHide.sections),
      )
    }

    if (node.children?.length) {
      node.children = skipElementsInTree(node.children, idsToHide)
    }
  }

  return localTree
}

export function moveNodeTo<T extends TreeLike>(
  tree: T[],
  nodeId: string | number,
  siblingId?: string | number,
  insertLocation: 'before' | 'after' = 'before',
): T[] {
  // Clone the tree to ensure immutability
  const insertAfter = insertLocation === 'after'
  let t = cloneDeep(tree)

  // Find the node to move
  const nodeToMove = findNodeInTreeById(t, nodeId)
  if (!nodeToMove) {
    throw new Error(`Node with id ${nodeId} not found`)
  }

  // Find the parent of the node to move
  const parentOfNodeToMove = findParentByNodeId(t, nodeId)

  // Remove the node from its current parent's children
  if (parentOfNodeToMove) {
    parentOfNodeToMove.children = parentOfNodeToMove.children?.filter((child) => child.id !== nodeId)
  } else {
    t = t.filter((node) => node.id !== nodeId)
  }

  if (siblingId === undefined) {
    t.push(nodeToMove)
    return t
  }

  const siblingNode = findNodeInTreeById(t, siblingId)
  if (!siblingNode) {
    throw new Error(`Sibling with id ${siblingId} not found`)
  }

  // WORKAROUND: If the sibling node is of type GridItem, add the node to move as its child
  if (siblingNode.type === ComponentTypes.GridItem && nodeToMove.type !== ComponentTypes.GridItem) {
    if (!siblingNode.children) {
      siblingNode.children = []
    }
    siblingNode.children.push(nodeToMove)
    return t
  }

  // Find the parent of the sibling node
  const parentOfSiblingNode = findParentByNodeId(t, siblingId)
  if (!parentOfSiblingNode) {
    const index = t.findIndex((node) => node.id === siblingId)
    t.splice(insertAfter ? index + 1 : index, 0, nodeToMove)
  } else {
    if (!parentOfSiblingNode.children) {
      parentOfSiblingNode.children = []
    }
    const index = parentOfSiblingNode.children.findIndex((node) => node.id === siblingId)
    parentOfSiblingNode.children.splice(insertAfter ? index + 1 : index, 0, nodeToMove)
  }

  return t
}
